/**
 * QA Meter CSS styles — a `:host` block with CSS variables prefixed `--mqa-`.
 *
 * The QA Meter z-index is set one below the feedback widget's `2147483640`
 * so the feedback FAB wins overlaps.
 *
 * Color tokens (hardcoded to remove dependency on DaisyUI HSL variables):
 *   green  #16a34a
 *   yellow #eab308
 *   red    #dc2626
 *   gray   #9ca3af
 *
 * Selectors ported from qa-pastille.vue, qa-test-panel.vue, qa-lane-icons.vue,
 * and qa-sitemap-modal.vue — adapted for standalone Shadow DOM use.
 */
export const QA_METER_STYLES = `
:host {
  --mqa-z-index: 2147483639;
  --mqa-z-panel: 2147483638;

  /* Color tokens */
  --mqa-green: #16a34a;
  --mqa-yellow: #eab308;
  --mqa-red: #dc2626;
  --mqa-gray: #9ca3af;

  /* Surface */
  --mqa-bg: #ffffff;
  --mqa-surface: #f9fafb;
  --mqa-border: #e5e7eb;
  --mqa-text: #111827;
  --mqa-text-muted: #6b7280;
  --mqa-primary: #3b82f6;
  --mqa-shadow: 0 4px 12px rgba(0,0,0,0.08), 0 2px 4px rgba(0,0,0,0.12);
  --mqa-shadow-xl: 0 24px 56px rgba(0,0,0,0.18), 0 8px 20px rgba(0,0,0,0.10);

  /* Font */
  --mqa-font: system-ui, -apple-system, sans-serif;
  --mqa-radius: 8px;

  all: initial;
  font-family: var(--mqa-font);
  color: var(--mqa-text);
  position: fixed;
  z-index: var(--mqa-z-index);
}

@media (prefers-color-scheme: dark) {
  :host {
    --mqa-bg: #1e293b;
    --mqa-surface: #0f172a;
    --mqa-border: #334155;
    --mqa-text: #f8fafc;
    --mqa-text-muted: #94a3b8;
    --mqa-shadow-xl: 0 24px 56px rgba(0,0,0,0.55), 0 8px 20px rgba(0,0,0,0.40);
  }
}

/* ── Semaphore modifier classes ─────────────────────────────────────────── */

.qa--green  { background: var(--mqa-green); }
.qa--yellow { background: var(--mqa-yellow); }
.qa--red    { background: var(--mqa-red); }
.qa--gray   { background: var(--mqa-gray); }

/* Priority modifiers (color only — components set sizing). */
.qa-prio--p0 { color: var(--mqa-red); }
.qa-prio--p1 { color: var(--mqa-yellow); }
.qa-prio--p2 { color: var(--mqa-green); }
.qa-prio--p3 { color: var(--mqa-gray); }

/* ── Pastille (FAB circle) ──────────────────────────────────────────────── */

/* The wrap is the fixed, bottom-right anchor for the whole pastille cluster.
   It MUST be a positioning context: the hover panel .qa-panel is
   position:absolute and is a SIBLING of the pastille, so without a positioned
   wrap it would anchor to :host (fixed, no offsets — i.e. the top-left of the
   screen) instead of sitting above the FAB. The --mqa-right/--mqa-bottom knobs
   (set on the host, inherited here) keep driving the on-screen position. The
   fixed sitemap modal inside the wrap anchors to the viewport regardless, so
   it's unaffected. */
.qa-pastille-wrap {
  position: fixed;
  right: var(--mqa-right, 1.5rem);
  bottom: var(--mqa-bottom, 1.5rem);
  z-index: var(--mqa-z-index);
}

.qa-pastille {
  position: relative;
}

.qa-pastille__dot {
  width: 48px;
  height: 48px;
  border-radius: 9999px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: var(--mqa-shadow);
  color: rgba(0, 0, 0, 0.5);
  /* No idle animation — the pastille stays static until interacted with, like
     the feedback FAB. (Previously pulsed on a perpetual keyframe loop.) Only
     the hover transition below animates. */
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  border: none;
}

.qa-pastille__dot:hover {
  transform: scale(1.12);
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.3);
}

.qa-pastille__ecg {
  width: 30px;
  height: 15px;
}

/* Secondary size: half-scale pastille for the integrated second-FAB use. */
:host([data-mqa-size="sm"]) .qa-pastille__dot { width: 24px; height: 24px; }
:host([data-mqa-size="sm"]) .qa-pastille__ecg { width: 15px; height: 8px; }

/* Color overrides: on colored backgrounds the ECG trace needs a dark stroke. */
.qa-pastille__dot.qa--green,
.qa-pastille__dot.qa--yellow,
.qa-pastille__dot.qa--red {
  color: rgba(0, 0, 0, 0.6);
}

.qa-pastille__dot.qa--gray {
  background: rgba(156, 163, 175, 0.35);
  color: rgba(0, 0, 0, 0.45);
}

.qa-fade-enter-active,
.qa-fade-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}
.qa-fade-enter-from,
.qa-fade-leave-to {
  opacity: 0;
  transform: translateY(4px);
}

@media (prefers-reduced-motion: reduce) {
  .qa-pastille__dot { transition: none; }
}

/* ── Test panel ─────────────────────────────────────────────────────────── */

/* The hover panel anchors above the pastille inside the fixed
   .qa-pastille-wrap. The ANCHOR (not the panel) is what's taken out of flow:
   it MUST be absolute so the wrap shrink-wraps to the pastille dot — an in-flow
   panel would stretch the wrap to 340px and either push the dot aside or (on a
   tall, scrolled host page) drive a scrollbar-reflow feedback loop that makes
   the FAB jump. (The donor's positioning lived on an orphaned
   .qa-pastille__panel selector that matched nothing after the Vue→Preact port
   renamed the root to .qa-panel.)

   The anchor's padding-bottom renders the 0.5rem visual gap between the dot
   and the panel AS PART of the hover region, so sweeping the cursor from the
   dot up onto the panel never crosses dead space → never fires the wrap's
   mouseleave → no flicker. (Paired with a small close-delay in mount.tsx.) */
.qa-panel-anchor {
  position: absolute;
  bottom: 100%;
  right: 0;
  padding-bottom: 0.5rem;
  z-index: var(--mqa-z-panel);
}

.qa-panel {
  background: var(--mqa-bg);
  border-radius: var(--mqa-radius);
  border: 1px solid var(--mqa-border);
  box-shadow: var(--mqa-shadow-xl);
  width: 340px;
  max-height: 60vh;
  overflow: hidden auto;
  font-size: 0.8125rem;
  color: var(--mqa-text);
}

.qa-panel__empty {
  padding: 0.75rem;
  opacity: 0.7;
}

.qa-panel__list,
.qa-panel__groups {
  margin: 0;
  padding: 0.25rem 0;
  list-style: none;
}

.qa-panel__foot {
  border-top: 1px solid var(--mqa-border);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  position: sticky;
  bottom: 0;
  background: var(--mqa-bg);
}

.qa-panel__count {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-size: 0.6875rem;
  opacity: 0.65;
}

.qa-panel__link {
  flex: none;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  color: var(--mqa-primary);
  background: none;
  border: none;
  font: inherit;
}

.qa-panel__link:hover { text-decoration: underline; }

/* ── Suite groups ───────────────────────────────────────────────────────── */

.qa-suite + .qa-suite { border-top: 1px solid var(--mqa-border); }

.qa-suite__head {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  text-align: left;
  background: none;
  border: none;
  font: inherit;
  color: inherit;
}

.qa-suite__head:hover { background: var(--mqa-surface); }

.qa-suite__chev {
  flex: none;
  font-size: 0.625rem;
  opacity: 0.6;
  transition: transform 0.12s ease;
}

.qa-suite__chev.is-open { transform: rotate(90deg); }

.qa-suite__key {
  flex: none;
  width: 1.25rem;
  height: 1.25rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.6875rem;
  font-weight: 700;
  background: var(--mqa-surface);
  border-radius: 4px;
}

.qa-suite__label {
  flex: 1;
  min-width: 0;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.qa-suite__count {
  flex: none;
  font-size: 0.6875rem;
  opacity: 0.55;
}

.qa-suite__list {
  background: var(--mqa-surface);
  margin: 0;
  padding: 0 0 0.25rem;
  list-style: none;
}

/* ── Scenario rows ──────────────────────────────────────────────────────── */

.qa-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.5rem 0.75rem;
}

.qa-row + .qa-row { border-top: 1px solid var(--mqa-border); }

.qa-row__title {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  min-width: 0;
}

.qa-row__name {
  flex: 1;
  min-width: 0;
  font-weight: 600;
  line-height: 1.25;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.qa-suite__list .qa-row { padding-left: 1.5rem; }

.qa-tag {
  flex: none;
  font-size: 0.625rem;
  padding: 0 0.25rem;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  opacity: 0.8;
  background: var(--mqa-surface);
  border-radius: 9999px;
}

/* ── Lane icons ─────────────────────────────────────────────────────────── */

.qa-lanes {
  display: inline-flex;
  align-items: center;
  gap: 0.3125rem;
  flex: none;
  padding: 0.1875rem 0.4375rem;
  border-radius: 9999px;
  background: rgba(17, 24, 39, 0.06);
}

.qa-lanes--sm { font-size: 0.875rem; }
.qa-lanes--md { font-size: 1rem; }

.qa-lanes__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: rgba(17, 24, 39, 0.4);
}

.qa-lanes__svg {
  width: 1em;
  height: 1em;
  display: block;
}

.qa-lanes__icon.qa--green  { color: var(--mqa-green); background: none; }
.qa-lanes__icon.qa--yellow { color: var(--mqa-yellow); background: none; }
.qa-lanes__icon.qa--gray   { color: rgba(17, 24, 39, 0.4); background: none; }

/* ── Sitemap modal backdrop ─────────────────────────────────────────────── */

.qa-modal__backdrop {
  background-color: rgba(17, 24, 39, 0.4);
  backdrop-filter: blur(1px);
  position: fixed;
  inset: 0;
  z-index: var(--mqa-z-index);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

/* ── Sitemap modal ──────────────────────────────────────────────────────── */

.qa-modal {
  background: var(--mqa-bg);
  border-radius: var(--mqa-radius);
  box-shadow: var(--mqa-shadow-xl);
  border: 1px solid var(--mqa-border);
  width: min(720px, 92vw);
  max-height: 86vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  color: var(--mqa-text);
}

.qa-modal__head {
  background: var(--mqa-primary);
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 0.75rem;
}

.qa-modal__head h3 {
  font-weight: 700;
  font-size: 1rem;
  margin: 0;
}

.qa-modal__close {
  border: 1px solid rgba(255, 255, 255, 0.3);
  background: transparent;
  color: #ffffff;
  width: 24px;
  height: 24px;
  padding: 0;
  border-radius: var(--mqa-radius);
  cursor: pointer;
  display: grid;
  place-items: center;
}

.qa-modal__close:hover { background: var(--mqa-red); }

.qa-modal__freshness {
  background: var(--mqa-surface);
  padding: 0.375rem 0.75rem;
  font-size: 0.6875rem;
  opacity: 0.8;
}

.qa-modal__body {
  overflow: auto;
  padding: 0.5rem 0;
}

/* ── KPI strip ──────────────────────────────────────────────────────────── */

.qa-kpi {
  display: flex;
  gap: 1.5rem;
  padding: 0.625rem 0.75rem;
}

.qa-kpi__item {
  display: flex;
  flex-direction: column;
}

.qa-kpi__value {
  font-size: 1.25rem;
  font-weight: 700;
  line-height: 1;
}

.qa-kpi__label {
  font-size: 0.6875rem;
  opacity: 0.7;
}

/* ── Tabs ───────────────────────────────────────────────────────────────── */

.qa-tabs {
  border-bottom: 1px solid var(--mqa-border);
  display: flex;
  gap: 0.25rem;
  padding: 0 0.5rem;
}

.qa-tabs__tab {
  padding: 0.375rem 0.75rem;
  font-weight: 600;
  font-size: 0.8125rem;
  border-bottom: 2px solid transparent;
  cursor: pointer;
  opacity: 0.6;
  background: none;
  border-left: none;
  border-right: none;
  border-top: none;
  font: inherit;
  color: inherit;
}

.qa-tabs__tab--active {
  color: var(--mqa-primary);
  border-bottom-color: var(--mqa-primary);
  opacity: 1;
}

/* ── Sitemap page tree ──────────────────────────────────────────────────── */

.qa-tree {
  margin: 0;
  padding: 0;
  list-style: none;
  font-size: 0.8125rem;
}

.qa-tree__row {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding-top: 0.25rem;
  padding-bottom: 0.25rem;
  padding-right: 0.75rem;
}

.qa-tree__row--here { background: var(--mqa-surface); }

.qa-tree__caret {
  width: 1rem;
  text-align: center;
  cursor: pointer;
  opacity: 0.6;
  font-size: 0.6875rem;
  background: none;
  border: none;
  font: inherit;
  color: inherit;
}

.qa-tree__caret--leaf { cursor: default; }

.qa-tree__title { flex: 1; }
.qa-tree__title--structural { font-style: italic; opacity: 0.75; }

.qa-tree__here {
  color: var(--mqa-primary);
  font-size: 0.625rem;
  font-weight: 700;
  text-transform: uppercase;
}

.qa-tree__count {
  background: var(--mqa-surface);
  border-radius: 9999px;
  font-size: 0.6875rem;
  padding: 0 0.375rem;
  min-width: 1.25rem;
  text-align: center;
}

/* ── Scenarios tab ──────────────────────────────────────────────────────── */

.qa-scenarios { padding: 0 0.75rem; }

.qa-scenarios__search {
  border: 1px solid var(--mqa-border);
  border-radius: var(--mqa-radius);
  width: 100%;
  height: 2rem;
  padding: 0 0.75rem;
  font-size: 0.8125rem;
  margin-bottom: 0.5rem;
  background: var(--mqa-bg);
  color: var(--mqa-text);
  font-family: inherit;
  box-sizing: border-box;
}

.qa-scenarios__empty {
  opacity: 0.7;
  padding: 0.5rem 0;
}

.qa-scenarios__list {
  margin: 0;
  padding: 0;
  list-style: none;
}

.qa-scenarios__item {
  border-top: 1px solid var(--mqa-border);
  padding: 0.5rem 0;
}

.qa-scenarios__line {
  display: flex;
  align-items: center;
  gap: 0.375rem;
}

.qa-scenarios__title {
  flex: 1;
  font-weight: 600;
  font-size: 0.8125rem;
}

.qa-scenarios__pages {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  font-size: 0.6875rem;
  opacity: 0.7;
  margin-top: 0.125rem;
  padding-left: 1rem;
}

/* ── Scenario dot (color indicator) ────────────────────────────────────── */

.qa-dot {
  width: 0.625rem;
  height: 0.625rem;
  border-radius: 9999px;
  flex: none;
  background: rgba(17, 24, 39, 0.6);
}

/* Override modifier background for dot — dot uses background not text color. */
.qa-dot.qa--green  { background: var(--mqa-green); }
.qa-dot.qa--yellow { background: var(--mqa-yellow); }
.qa-dot.qa--red    { background: var(--mqa-red); }
.qa-dot.qa--gray   { background: var(--mqa-gray); }

/* ── Suite key badge ────────────────────────────────────────────────────── */

.qa-suitekey {
  flex: none;
  font-size: 0.625rem;
  font-weight: 700;
  padding: 0 0.3125rem;
  line-height: 1.4;
  background: var(--mqa-surface);
  border-radius: 4px;
}
`
